<script setup lang="ts">
import Modal from '@/components/modal/Modal.vue';

import { reactive, provide, computed, onMounted, watch, h } from 'vue';
import { newDefaultData } from '@/appLogic/importData/initialData';
import { businessContentName } from '@/appLogic/business/businessTableHeaders';
import { ref } from 'vue';
import { i18n } from '@/i18n';
import { OnPreLoadedTableKey as onPreLoadedTableKey, onCheckIdsKey, openModalKey } from '@/injectStore/tableContent';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SimpleBusinessTableContent from '@/components/table/SimpleBusinessTableContent.vue';
import WorkDaysCalendarNotYetEnteredAlertModal from '@/components/modal/WorkDaysCalendarNotYetEnteredAlertModal.vue';
import { shouldShowWorkDaysCalendarNotEnteredAlertModal } from '@/appLogic/importData/calendar';
import ControlPageTab from '@/corePagesModule/components/ControlPageTab.vue';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { OrganizationBusinessRepository } from '@/businessRepository/organizationBusinessRepository';
import { BulkGetBusinessUseCase } from '@/businessUseCases/bulkGetBusinessUseCase';
import { BulkRegisterBusinessUseCase } from '@/businessUseCases/bulkRegisterBusinessUseCase';
import { ForecastStatusManager } from '@/appLogic/business/businessState';
import AddVariableTableContent from '@/corePagesModule/addVariableTableContent/pages/index.vue';
import {
  openConfirmModal,
  openFailToSavedModal,
  openOkInformationModal,
  openSuccessToSaveModal,
} from '@/corePagesModule/components/modal/modalActions';
import { useOrderControlStore } from '../orderControlStore';
import { storeToRefs } from 'pinia';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { NarrowState } from '@/corePagesModule/BaseTableContent/interface/uiState';
import { TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import { useAggregateBusinessIdHasRecommendOrderStore } from '@/corePagesModule/store/useAggregateBusinessIdHasRecommendOrderStore';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { FilterContentItem, FilterModalItems, FreeColumnFilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { diContainer } from '@/coreContainer/container';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import DisplayOrderListMenuDropDown from '@/orderControlModule/components/DisplayOrderListMenuDropDown.vue';
import { DisplayOrderListMenuItems } from '../entities/displayOrderListMenu';
import UpdateToApprovedMenuDropDown from '@/orderControlModule/components/UpdateToApprovedMenuDropDown.vue';
import { UpdateToApprovedMenuItems } from '../entities/updateToApprovedMenu';
import OrderStatusFilterButtons from './OrderStatusFilterButtons.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import DropdownButton from '@/corePagesModule/components/DropdownButton.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';

/**
 * モーダルの種類を定義する
 */
type ModalType = 'edit' | 'forecast-detail' | 'sales-results' | 'register-from-copy' | 'eop-results' | 'order-form' | 'confirm-supplier';
const organizationBusinessRepository = new OrganizationBusinessRepository();
const bulkGetBusinessUseCase = new BulkGetBusinessUseCase();
const bulkRegisterBusinessUseCase = new BulkRegisterBusinessUseCase();

/**
 * 登録テーブルの状態を定義するインターフェース
 */
interface IRegistrationTable {
  registrationModalState: boolean;
  editModalState: boolean;
  forecastDetailModalState: boolean;
  salesResultsModalState: boolean;
  orderFormModalState: boolean;
  confirmSupplierModalState: boolean;
  updateTable: boolean;
  formUseItem: IBusiness;
  checkedIds: string[];
  checkedBusinesses: IBusiness[];
  registerFromCopy: boolean;
  tableContentHeader: object;
  updateOrderStatusModalState: boolean;
  workDaysCalendarNotYetEnteredAlertModalStatus: boolean;
  addVariableContentModalState: boolean;
  getAggregateBusinessIdsHasRecommendOrderErrorModalState: boolean;
  isOpenDisplayOrderListMenuDropdown: boolean;
  selectedDisplayOrderListMenu: DisplayOrderListMenuItems | undefined;
}

// 変数
const orderControlStore = useOrderControlStore();
const { state: orderControlState } = storeToRefs(orderControlStore);
const {
  $reset,
  onChangePaging,
  sortTableContentItems,
  resetPagingOptions,
  fetchItems,
  onAppliedFilterModal,
  filterTableContentItems,
  fetchTableContentProperties,
  changeOrderStatusFilterItems,
  containsRecommendOrder,
  getFilterItemsValue,
  getFreeColumnFilterItemsValue,
  putHiddenRecommended,
} = orderControlStore;

const aggregateBusinessIdsHasRecommendOrderStore = useAggregateBusinessIdHasRecommendOrderStore();
const { state: aggregateBusinessIdsHasRecommendOrderState } = storeToRefs(aggregateBusinessIdsHasRecommendOrderStore);
const { fetchAggregateBusinessIdsHasRecommendOrder, fetchAggregateBusinessIdsHasApprovedRequestOrder } =
  aggregateBusinessIdsHasRecommendOrderStore;
const t = i18n.t;
const tableContent = ref(null);

let renderingKey = 0;
const isEditing = ref(false);
const isRefreshTable = ref(false);

// mounted
onMounted(async () => {
  await init();
  state.workDaysCalendarNotYetEnteredAlertModalStatus = await shouldShowWorkDaysCalendarNotEnteredAlertModal();
});

/**
 * 初期化処理
 */
const init = async () => {
  $reset();
  fetchItems();
  // 推奨情報を持つ集計ID一覧を取得する
  aggregateBusinessIdsHasRecommendOrderStore.$reset(); // 情報の初期化
  // fetchAggregateBusinessIdsHasRecommendOrder(orderControlState.value.filterModalItems);
  orderControlState.value.tableContentProperties = fetchTableContentProperties();

  renderingKey++;
};

/**
 * チェックされている全ての発注が承認依頼（ApprovalRequested）ステータスかどうかを判定
 */
const areAllCheckedItemsApprovalRequested = computed((): boolean => {
  const checkedItems = orderControlState.value.tableContentItems.filter((item) => state.checkedIds.includes(item.displayId));
  if (checkedItems.length === 0) {
    return false;
  }
  return checkedItems.every((item) => item.orderStatus === OrderStatus.ApprovalRequested);
});

const areAllCheckedItemsRecommended = computed(() => {
  const checkedItems = orderControlState.value.tableContentItems.filter((item) => state.checkedIds.includes(item.displayId));
  if (checkedItems.length === 0) return false;
  return checkedItems.every((item) => item.orderStatus === OrderStatus.Recommended);
});

// state変数
const state: IRegistrationTable = reactive<IRegistrationTable>({
  registrationModalState: false,
  editModalState: false,
  forecastDetailModalState: false,
  salesResultsModalState: false,
  orderFormModalState: false,
  confirmSupplierModalState: false,
  updateTable: false,
  formUseItem: newDefaultData,
  checkedIds: [],
  checkedBusinesses: [],
  registerFromCopy: false,
  tableContentHeader: businessContentName,
  updateOrderStatusModalState: false,
  workDaysCalendarNotYetEnteredAlertModalStatus: false,
  addVariableContentModalState: false,
  getAggregateBusinessIdsHasRecommendOrderErrorModalState: false,
  isOpenDisplayOrderListMenuDropdown: false,
  selectedDisplayOrderListMenu: undefined,
}) as IRegistrationTable;

// watcher
watch(
  () => aggregateBusinessIdsHasRecommendOrderState.value.loadingState.status.type,
  async () => {
    if (aggregateBusinessIdsHasRecommendOrderState.value.loadingState.status.type === 'error') {
      state.getAggregateBusinessIdsHasRecommendOrderErrorModalState = true;
    }
  }
);

// methods
/**
 * フィルターモーダルのアイテムを取得する処理
 */
const getFilterModalItems = (): FilterModalItems => ({
  selectionFilterItems: getFilterItemsValue().value,
  freeColumnFilterItems: getFreeColumnFilterItemsValue().value,
});

/**
 * チェックボックスの状態を更新する処理
 * @param checkIds - チェックされた行のID一覧
 */
const onCheck = async (checkIds: string[]) => {
  state.checkedIds = checkIds;
};

/**
 * ページ遷移を行う処理
 * TODO: 新IBusinessへの以降処理前でごちゃごちゃするのを避けるため、一旦旧IBusinessの型で置いている
 * 本当はstoreに括り出すべきでは？
 * @param event -  取引情報
 */
const sendRecommendOrderPageMethod = (event: string[]) => {
  const id = event ? event[0] : [];
  // 集計ID一覧をlocalStorageに保存しておく
  const result = diContainer.saveAggregateBusinessIdsForRecommendOrderUseCase.Execute(event);
  if (result.status.type === 'error') {
    toast.error(t('集計情報の保存に失敗しました'));
    return;
  }
  window.open(`#/business/recommend-order/list/${id}`, '_blank');
};

/**
 * モーダルを開く処理
 * @param event - 取引情報
 * @param type - モーダルの種類
 */
const openModal = (event: IBusiness, type: ModalType) => {
  state.formUseItem = Object.assign({}, JSON.parse(JSON.stringify(event)));

  switch (type) {
    case 'edit':
      state.editModalState = true;
      break;
    case 'forecast-detail':
      state.forecastDetailModalState = true;
      break;
    case 'sales-results':
      state.salesResultsModalState = true;
      break;
    case 'register-from-copy':
      // 既存商品の情報をもとに新規作成を行う
      state.registerFromCopy = true;
      state.registrationModalState = true;
      break;
    case 'order-form':
      state.orderFormModalState = true;
      break;
    case 'confirm-supplier':
      state.confirmSupplierModalState = true;
      break;
  }
};

/**
 * Excelインポートモーダルを閉じる処理
 */
const onComplete = () => {
  state.updateTable = true;
  state.checkedIds = [];
  (tableContent.value as any).clearCheckIds();
};

/**
 * チェックされた取引内容の情報をdbから取得
 * @returns チェックが付いている行の取引内容情報
 */
const getCheckedBusinesses = async (): Promise<IBusiness[]> => {
  const res = await bulkGetBusinessUseCase.Execute(organizationBusinessRepository, state.checkedIds);
  switch (res.status.type) {
    case 'success':
      return res.status.data;
    case 'error':
  }
  return [] as IBusiness[];
};
/**
 * 確認モーダルを非表示
 */
const closeModal = () => {
  onComplete();
  state.confirmSupplierModalState = false;
  state.updateOrderStatusModalState = false;
};

/**
 * 稼働日カレンダー未入力警告モーダルを非表示
 */
const closeWorkDaysCalendarNotYetEnteredAlertModal = () => {
  state.workDaysCalendarNotYetEnteredAlertModalStatus = false;
};
/**
 * 仕入先内示として確定させる取引を確定する
 */
const submitSupplierForecast = async () => {
  // 仕入先内示へ追加
  const updatedBusinesses: IBusiness[] = await ForecastStatusManager.addSuppliersForecast(await getCheckedBusinesses());
  updatedBusinesses.map((business) => (business.supplierAnnounce = business.customerAnnounce));
  const res = await bulkRegisterBusinessUseCase.Execute(organizationBusinessRepository, updatedBusinesses);
  switch (res.status.type) {
    case 'success':
      // ステータスの更新が完了したらモーダルを閉じる
      closeModal();
      break;
    case 'error':
    // TODO: implement
  }
};

/**
 * 確定発注ステータスを強制的に書き換える
 * 取引一覧に表示されているもので、確定発注を作成しない取引ステータスを「確定発注に作成しない」ステータスに変更する
 * TODO: 確定発注作成フローが完成次第forceUpdateOrderState()に変更する (現状、確定発注を作成する機構自体がないため、作成済みに倒すように置いてます)
 */
const forceUpdateConfirmOrder = async () => {
  // ステータス更新
  const updatedBusinesses = await ForecastStatusManager.addConfirmedOrder(await getCheckedBusinesses());
  // ステータス更新後の取引情報を登録
  const res = await bulkRegisterBusinessUseCase.Execute(organizationBusinessRepository, updatedBusinesses);
  switch (res.status.type) {
    case 'success':
      // ステータスの更新が完了したらモーダルを閉じる
      closeModal();
      break;
    case 'error':
    // TODO: implement
  }
};

/**
 * プロパティ追加モーダルを非表示
 */
const onSuccessSaveVariableContent = async () => {
  state.addVariableContentModalState = false;
  await openSuccessToSaveModal();
  // テーブルの再読み込み
  state.updateTable = true;
};

/**
 * プロパティ追加モーダルでの保存に失敗した場合の処理
 */
const onFailSaveVariableContent = async () => {
  state.addVariableContentModalState = false;
  await openFailToSavedModal();
};

/**
 * チェックがついている発注のorderStatusを承認済みに変更する
 */
const approveCheckedOrders = async () => {
  const modalResult = await openConfirmModal(
    'question',
    t('発注承認モーダル文', { number: state.checkedIds.length }),
    t('はい'),
    t('いいえ')
  );

  if (modalResult === undefined || modalResult.isConfirmed === false) {
    return;
  }

  orderControlState.value.updateOrderLoadingState.status.type = 'loading';

  // チェックされたdisplayIdをもとに取引情報から発注IDを取得する
  const checkedOrderIds = orderControlState.value.tableContentItems
    .filter((item) => state.checkedIds.includes(item.displayId))
    .map((item) => item.orderId)
    .filter((id) => id != null && id !== ''); // 推奨発注情報はorderIdが空文字なので、フィルターで弾いておく

  const updateOrderStatusResponse = await diContainer.updateOrderStatusUseCase.Execute(
    checkedOrderIds,
    OrderStatus.ApprovedAwaitingDeliveryDate
  );
  switch (updateOrderStatusResponse.status.type) {
    case 'success':
      resetPagingOptions();
      await fetchItems();
      state.checkedIds = [];
      renderingKey++;
      orderControlState.value.updateOrderLoadingState.status.type = 'success';
      await openSuccessToSaveModal();
      return;
    case 'error':
      orderControlState.value.updateOrderLoadingState.status.type = 'error';
      openFailToSavedModal();
      return;
  }
};

/**
 * チェックがついている発注を削除する
 */
const deleteApprovalRequest = async () => {
  const modalResult = await openConfirmModal('question', t('承認依頼を削除しますか？'), t('はい'), t('いいえ'));

  if (modalResult === undefined || modalResult.isConfirmed === false) {
    return;
  }

  orderControlState.value.updateOrderLoadingState.status.type = 'loading';

  // チェックされたdisplayIdをもとに取引情報から発注IDを取得する
  const checkedOrderIds = orderControlState.value.tableContentItems
    .filter((item) => state.checkedIds.includes(item.displayId))
    .map((item) => item.orderId)
    .filter((id) => id != null && id !== ''); // 推奨発注情報はorderIdが空文字なので、フィルターで弾いておく

  const deleteOrderResponse = await diContainer.bulkDeleteOrderUseCase.Execute(checkedOrderIds);
  switch (deleteOrderResponse.status.type) {
    case 'success':
      resetPagingOptions();
      await fetchItems();
      state.checkedIds = [];
      renderingKey++;
      orderControlState.value.updateOrderLoadingState.status.type = 'success';
      await openSuccessToSaveModal();
      return;
    case 'error':
      orderControlState.value.updateOrderLoadingState.status.type = 'error';
      openFailToSavedModal();
      return;
  }
};

/**
 * 承認済に更新メニューが選択された際の処理
 * @param menuItem - 承認済に更新メニューの項目
 */
const onSelectedUpdateToApprovedMenu = async (menuItem: UpdateToApprovedMenuItems) => {
  if (menuItem === 'UpdateToApproved') {
    await approveCheckedOrders();
  } else if (menuItem === 'DeleteApprovalRequest') {
    await deleteApprovalRequest();
  }
};

/**
 * 推奨情報を持つ集計ID一覧を再取得する処理
 */
const reRunFetchAggregateBusinessIdsHasRecommendOrder = async () => {
  state.getAggregateBusinessIdsHasRecommendOrderErrorModalState = false; // エラーモーダルを閉じる
  await waitLoading(fetchAggregateBusinessIdsHasRecommendOrder(getFilterModalItems())); // 推奨情報を持つ集計ID一覧を再取得
};

/**
 * 発注一括確認メニューが選択された際の処理
 * @param displayOrderListMenu - 発注一括確認メニュー
 */
const onSelectedDisplayOrderListMenu = async (displayOrderListMenu: DisplayOrderListMenuItems) => {
  if (displayOrderListMenu === 'Recommend') {
    const targetIds = await fetchAggregateBusinessIdsHasRecommendOrder(getFilterModalItems());
    if (targetIds.length === 0) {
      await openOkInformationModal('info', t('推奨情報がありません'), 'OK');
      return;
    } else {
      sendRecommendOrderPageMethod(targetIds);
    }
  } else if (displayOrderListMenu === 'ApprovalRequested') {
    const targetIds = await fetchAggregateBusinessIdsHasApprovedRequestOrder(getFilterModalItems());
    if (targetIds.length === 0) {
      await openOkInformationModal('info', t('承認依頼情報がありません'), 'OK');
      return;
    } else {
      sendRecommendOrderPageMethod(targetIds);
    }
  }
};

/**
 * フィルターボタンを押した時の処理
 * @param filterItems - フィルター選択内容
 * @param freeColumnFilterItems - 自由カラムのフィルター選択内容
 */
const filterAction = (filterItems: FilterContentItem[], freeColumnFilterItems: FreeColumnFilterContentItem[]) => {
  onAppliedFilterModal(filterItems, freeColumnFilterItems);
  // NOTE: フィルターを適用した後に、推奨情報を持つ集計ID一覧を再取得する (orderControlState.value.filterModalItemsを使う)
  // fetchAggregateBusinessIdsHasRecommendOrder(orderControlState.value.filterModalItems);
};

/**
 * 推奨の表示非表示管理ボタンを押した際のハンドラ
 * @param optionValue - 選択したオプション
 */
const onClickRecommendDisplayOption = async (optionValue: string) => {
  switch (optionValue) {
    case 'hidden':
      await onClickHiddenRecommended();
      return;
    case 'reveal':
      $reset();
      orderControlState.value.isHiddenRecommendShow = true;
      fetchItems();
      renderingKey++;
      return;
  }
};

/**
 * 推奨を非表示にするボタンクリック
 */
const onClickHiddenRecommended = async () => {
  const confirm = await showInfoModal({
    modalTitle: t('選択した推奨を非表示にしますか？'),
    modalDescription: h('div', { style: { width: 'max-content', textAlign: 'start', margin: '0 auto' } }, [
      t('・選択した推奨発注を非表示にします'),
      h('br'),
      t('・非表示期間は6日間です'),
    ]),
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('OK'),
  });
  if (!confirm) return;
  const res = await waitLoading(putHiddenRecommended(state.checkedIds, true));
  if (res) {
    showInfoModal({ modalTitle: t('保存しました'), backButtonLabel: t('OK'), isDisabledNextButton: true });
    state.checkedIds = [];
    init();
  } else {
    showInfoModal({ modalTitle: t('保存に失敗しました'), backButtonLabel: t('OK'), isDisabledNextButton: true });
  }
};

/**
 * 非表示推奨表示から通常の画面に戻る
 */
const onClickBackToDefault = () => {
  $reset();
  fetchItems();
  renderingKey++;
};

/**
 * 推奨を再表示するボタンクリック
 */
const onClickRevealRecommended = async () => {
  const confirm = await showInfoModal({
    modalTitle: t('選択した推奨を再表示しますか？'),
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('OK'),
  });
  if (!confirm) return;
  const res = await waitLoading(putHiddenRecommended(state.checkedIds, false));
  if (res) {
    showInfoModal({ modalTitle: t('保存しました'), backButtonLabel: t('OK'), isDisabledNextButton: true });
    state.checkedIds = [];
    init();
  } else {
    showInfoModal({ modalTitle: t('保存に失敗しました'), backButtonLabel: t('OK'), isDisabledNextButton: true });
  }
};

provide(openModalKey, openModal);
provide(onCheckIdsKey, onCheck);
provide(onPreLoadedTableKey, () => {});
provide(onPreLoadedTableKey, () => {});
</script>

<template>
  <div class="content-flame">
    <div class="md-layout">
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <Modal
          class="confirm-modal-container"
          :modal-size="'2xl'"
          :aspect-ratio="true"
          :stretch-container="true"
          :scroll-off="true"
          :padding="'16px'"
          :modal-state="state.updateOrderStatusModalState"
          @outside-click="closeModal"
        >
          <div class="confirm-modal-content confirm-modal-container__confirm-modal-content">
            <div class="confirm-modal-content__title">
              {{ t('発注ステータスを更新しますか？') }}
            </div>
            <SimpleBusinessTableContent
              class="confirm-modal-content__table"
              :table-type="'confirmSupplierTable'"
              :items="state.checkedBusinesses"
              :scroll-enable="true"
            />
            <div class="confirm-button-container">
              <TextButton
                :color="'primary'"
                :size="'btn-md'"
                class="confirm-button-container__confirm-button"
                @click="forceUpdateConfirmOrder"
              >
                {{ t('はい') }}
              </TextButton>
              <TextButton :color="'default'" :size="'btn-md'" class="confirm-button-container__confirm-button" @click="closeModal">
                {{ t('いいえ') }}
              </TextButton>
            </div>
          </div>
        </Modal>
        <Modal
          class="confirm-modal-container"
          :modal-size="'2xl'"
          :aspect-ratio="true"
          :stretch-container="true"
          :scroll-off="true"
          :padding="'16px'"
          :modal-state="state.confirmSupplierModalState"
          @outside-click="closeModal"
        >
          <div class="confirm-modal-content confirm-modal-container__confirm-modal-content">
            <div class="confirm-modal-content__title">
              {{ t('受領した内示を仕入先への内示としますか？') }}
            </div>
            <SimpleBusinessTableContent
              class="confirm-modal-content__table"
              :table-type="'confirmSupplierTable'"
              :items="state.checkedBusinesses"
              :scroll-enable="true"
            />
            <div class="confirm-button-container">
              <TextButton
                :color="'primary'"
                :size="'btn-md'"
                class="confirm-button-container__confirm-button"
                @click="submitSupplierForecast"
              >
                {{ t('はい') }}
              </TextButton>
              <TextButton :color="'default'" :size="'btn-md'" class="confirm-button-container__confirm-button" @click="closeModal">
                {{ t('いいえ') }}
              </TextButton>
            </div>
          </div>
        </Modal>
        <WorkDaysCalendarNotYetEnteredAlertModal
          :is-open-modal="state.workDaysCalendarNotYetEnteredAlertModalStatus"
          @outside-click="closeWorkDaysCalendarNotYetEnteredAlertModal"
        />
        <div>
          <div class="page-title">
            {{ t('管理表') }}
          </div>
          <div class="control-page__header">
            <ControlPageTab class="control-page-tab" />

            <template v-if="orderControlState.isHiddenRecommendShow">
              <TextButton
                button-style="btn-primary"
                size="btn-sm"
                :disabled="!areAllCheckedItemsRecommended"
                @click="onClickRevealRecommended"
              >
                {{ t('選択した推奨発注を再表示する') }}
              </TextButton>
              <TextButton button-style="btn-secondary" size="btn-sm" @click="onClickBackToDefault">
                {{ t('元の画面に戻る') }}
              </TextButton>
            </template>
            <template v-else>
              <!-- テーブル内で推奨・発注依頼・承認済みのフィルターをここで操作する -->
              <OrderStatusFilterButtons
                :filter-status="orderControlState.orderStatusFilterItems"
                :record-count="orderControlState.summaryCount"
                @update-order-status-filter="changeOrderStatusFilterItems"
              />

              <UpdateToApprovedMenuDropDown
                class="order-status-button__update-button"
                :disabled="
                  state.checkedIds.length === 0 ||
                  (!orderControlState.orderStatusFilterItems.Recommended && containsRecommendOrder(state.checkedIds)) ||
                  !areAllCheckedItemsApprovalRequested
                "
                @selected-item="
                  (item) => {
                    // 選択メニューの情報更新
                    onSelectedUpdateToApprovedMenu(item);
                  }
                "
              />
              <DisplayOrderListMenuDropDown
                class="order-status-button__update-button"
                :is-open-prop="state.isOpenDisplayOrderListMenuDropdown"
                :selected-item-prop="state.selectedDisplayOrderListMenu"
                @click:select-input="() => (state.isOpenDisplayOrderListMenuDropdown = true)"
                @close="() => (state.isOpenDisplayOrderListMenuDropdown = false)"
                @selected-item="
                  (item) => {
                    // 選択メニューの情報更新
                    onSelectedDisplayOrderListMenu(item);
                  }
                "
              />
              <DropdownButton
                :options="[
                  { label: t('推奨を非表示にする'), value: 'hidden', disabled: !areAllCheckedItemsRecommended },
                  { label: t('非表示推奨の確認・再表示'), value: 'reveal' },
                ]"
                :style="'secondary'"
                size="sm"
                @click-option="onClickRecommendDisplayOption"
              >
                <template #label>
                  <div class="recommend-display-dropdown-label">
                    <Mdi icon="visibility" variant="outlined" />
                    {{ t('推奨の表示・非表示') }}
                  </div>
                </template>
              </DropdownButton>
            </template>
          </div>

          <div class="control-page__top-section top-section">
            <div class="top-section__filter">
              <FilterModalButton
                class="top-section__filter"
                filter-modal-type="orderControl"
                :select-input-index="0"
                :filter-items="getFilterItemsValue().value"
                :free-column-filter-items="getFreeColumnFilterItemsValue().value"
                @apply="filterAction"
              />
            </div>

            <Pagination
              v-if="orderControlState.pagingOptions"
              :paging-options="orderControlState.pagingOptions"
              :is-loading="orderControlState.loadingState.status.type === 'loading'"
              class="pagination"
              @on-change-page="onChangePaging($event)"
            />
          </div>

          <NewBaseTableContent
            :key="renderingKey"
            :prop-table-content-properties="orderControlState.tableContentProperties"
            :prop-display-objects="orderControlState.tableContentItems"
            :prop-loading-status="orderControlState.loadingState"
            :prop-table-type="'orderManageTable'"
            :prop-is-editing="isEditing"
            :prop-is-reset="isRefreshTable"
            class="unordered-items-table__contains unordered-items-table__contains--table"
            @checked="state.checkedIds = $event"
            @on-click-sort="(sortKey: TableContentProperty, isAsc: boolean) => sortTableContentItems(sortKey, isAsc)"
            @on-click-filter="(narrowState: NarrowState[]) => filterTableContentItems(narrowState)"
            @reload="init"
          />
        </div>
        <Modal
          :stretch-container="true"
          modal-title="なんか入力して"
          modal-size="3xl"
          :modal-state="state.addVariableContentModalState"
          @outside-click="state.addVariableContentModalState = false"
        >
          <AddVariableTableContent
            :table-type="'baseTable'"
            @on-success="onSuccessSaveVariableContent"
            @on-error="onFailSaveVariableContent"
          />
        </Modal>
      </div>
    </div>
    <InfoModal :modal-state="isLoading(orderControlState.updateOrderLoadingState.status)" icon="loading" :enable-outside-click="false">
      <template #title>{{ t('データを保存しています') }}</template>
    </InfoModal>
    <InfoModal
      :modal-state="state.getAggregateBusinessIdsHasRecommendOrderErrorModalState"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('再実行')"
      icon="error"
      :enable-outside-click="false"
      @on-back="state.getAggregateBusinessIdsHasRecommendOrderErrorModalState = false"
      @on-next="reRunFetchAggregateBusinessIdsHasRecommendOrder"
    >
      <template #title>{{ t('推奨情報を持つ集計ID一覧取得に失敗しました') }}</template>
    </InfoModal>
  </div>
</template>

<style lang="scss" scoped>
// FIXME: 時間なかったので、仮置き ここのページ自体、デザイン変更は一番外から見直さないといけない(たぶんgridとかがおざなり)
.content-flame {
  margin-top: 16px;
}

// 枠線が残っていたのでなくす
.md-card {
  box-shadow: none;
}
.file-input {
  display: flex;
  margin: 0 10px;
}
.register-excel-button {
  animation: 0.1s fadeIn;
}
.output-excel-button {
  animation: 0.1s fadeOut;
}

.control-page {
  &__header {
    display: grid;
    grid-template-columns: repeat(5, auto);
    justify-content: start;
    gap: 24px;
    align-items: center;
    // FIXME: テーブルにマージン無いので、特急でこれ
    margin-top: 8px;
    margin-bottom: 16px;
  }

  &__top-section {
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 16px;
    align-items: center;
  }
}

.top-section {
  &__filter {
    margin-bottom: 16px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.5;
  }
}
@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.control-page-tab {
  margin-bottom: 2px;
}

.page-title {
  color: $textDefault;
  margin-bottom: 8px;
  font-family: Noto Sans JP;
  font-size: 22px;
  line-height: 31.86px;
  font-weight: 700;
  letter-spacing: 0px;
}
.recommend-display-dropdown-label {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  align-items: center;
  gap: 4px;
}
</style>
